import React, { useState, useEffect } from "react";
import styled from "styled-components";
import type { ArtistArtwork } from "./types";

export interface ArtworkFormValues {
  title: string;
  description: string;
  dateMade: string; // YYYY-MM-DD
  donation: boolean;
  image: File | null;
}

interface Props {
  initial?: Partial<ArtistArtwork>;
  submitLabel: string;
  isSubmitting?: boolean;
  requireImage?: boolean; // true on add, false on edit
  onSubmit: (values: ArtworkFormValues) => void;
  onCancel?: () => void;
}

function isoDateToInputValue(d?: string): string {
  if (!d) return "";
  try {
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
}

export const ArtworkForm: React.FC<Props> = ({
  initial,
  submitLabel,
  isSubmitting,
  requireImage,
  onSubmit,
  onCancel,
}) => {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [dateMade, setDateMade] = useState(isoDateToInputValue(initial?.dateMade));
  const [donation, setDonation] = useState(!!initial?.donation);
  const [image, setImage] = useState<File | null>(null);

  // re-sync when initial loads asynchronously (edit page after fetch)
  useEffect(() => {
    if (!initial) return;
    setTitle(initial.title || "");
    setDescription(initial.description || "");
    setDateMade(isoDateToInputValue(initial.dateMade));
    setDonation(!!initial.donation);
  }, [initial?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ title, description, dateMade, donation, image });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Field>
        <span>Title</span>
        <input
          type="text"
          value={title}
          required
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>

      <Field>
        <span>Description</span>
        <textarea
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      <Row>
        <Field $compact>
          <span>Date made</span>
          <input
            type="date"
            value={dateMade}
            onChange={(e) => setDateMade(e.target.value)}
          />
        </Field>

        <CheckboxField>
          <input
            id="donation"
            type="checkbox"
            checked={donation}
            onChange={(e) => setDonation(e.target.checked)}
          />
          <label htmlFor="donation">
            <strong>Donation</strong>
            <small>this piece is donated to the n450s site</small>
          </label>
        </CheckboxField>
      </Row>

      <Field>
        <span>
          Image{!requireImage && initial?.imageFilename && (
            <em> (leave empty to keep current)</em>
          )}
        </span>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setImage(e.target.files?.[0] ?? null)}
          required={!!requireImage}
        />
      </Field>

      <Actions>
        {onCancel && (
          <CancelBtn type="button" onClick={onCancel}>
            cancel
          </CancelBtn>
        )}
        <SubmitBtn type="submit" disabled={isSubmitting}>
          {isSubmitting ? "saving…" : submitLabel}
        </SubmitBtn>
      </Actions>
    </Form>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 560px;
`;

const Field = styled.label<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.9rem;
  ${(p) => (p.$compact ? "max-width: 240px;" : "")}

  span {
    font-weight: 700;
    em {
      font-weight: 400;
      opacity: 0.65;
    }
  }
  input,
  textarea {
    font-family: inherit;
    font-size: 1rem;
    padding: 8px 10px;
    border: 3px solid #000000;
    background: #ffffff;
    width: 100%;
    box-sizing: border-box;
    box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  }

  textarea {
    resize: vertical;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const CheckboxField = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: #fff77a;
  padding: 10px 14px;
  border: 3px solid #000000;
  box-shadow: 3px 4px rgba(0, 0, 0, 0.7);

  input {
    margin-top: 4px;
    transform: scale(1.3);
  }

  label {
    display: flex;
    flex-direction: column;
    line-height: 1.2;

    strong {
      font-size: 0.95rem;
      font-weight: 700;
    }
    small {
      font-size: 0.78rem;
      opacity: 0.75;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 4px;
`;

const SubmitBtn = styled.button`
  padding: 8px 18px;
  font-size: 0.95rem;
  font-weight: 700;
  font-family: inherit;
  background: #fff77a;
  color: #000000;
  border: 3px solid #000000;
  box-shadow: 4px 6px rgba(0, 0, 0, 0.7);
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease;

  &:hover:not(:disabled) {
    transform: translate(1px, 2px);
    box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelBtn = styled.button`
  padding: 8px 18px;
  font-size: 0.95rem;
  font-weight: 700;
  font-family: inherit;
  background: #ffffff;
  color: #000000;
  border: 3px solid #000000;
  box-shadow: 4px 6px rgba(0, 0, 0, 0.7);
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease;

  &:hover {
    transform: translate(1px, 2px);
    box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  }
`;

export default ArtworkForm;
